import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";

import useFilterStore from "@/stores/useFilterStore.js";

import CustomBadge from "@/components/desktop/badge/CustomBadge";
import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import useListController from "@/hooks/list/useListController";
import { useTolerances, useToleranceLookups } from "@/hooks/maintainers/OA/Tolerances/useTolerances";

import { base_table, base_object } from "./tolerance.constants";
import ToleranceForm from "./Form/ToleranceForm";

const Tolerances = () => {
	const navigate = useNavigate();
	const { filter } = useFilterStore();
	const { estate_parent_id, estate_id, search, parameter } = filter;

	const { tableState, reactTableSort, handlePageChange, handlePageSizeChange, handleSortByChange } = useListController({
		baseFilter: [],
		baseTable: base_table,
	});

	const filters = {
		estate_id,
		estate_parent_id,
		search,
		parameter,
	};

	const { data: lookups, isLoading: isLoadingLookups } = useToleranceLookups(filters);
	const { data: toleranceData, isLoading: isLoadingTolerances } = useTolerances(filters);

	const rows = useMemo(() => {
		const backendList = toleranceData?.data ?? [];
		const toleranceMap = new Map(backendList.map((t) => [String(t.estate_id), t]));

		return (lookups?.estates ?? []).map((estate) => ({
			estate_id: estate.value,
			estate_name: estate.label,
			...(toleranceMap.get(String(estate.value)) ?? base_object),
		}));
	}, [lookups, toleranceData]);

	const columns = useMemo(
		() => [
			{
				id: "index",
				Header: "#",
				disableSortBy: true,
				width: "4%",
				Cell: ({ row }) => <span>{(tableState.page - 1) * tableState.per_page + row.index + 1}</span>,
			},
			{
				Header: "Bodega",
				accessor: "estate_name",
				width: "35%",
				disableSortBy: true,
			},
			{
				Header: "Margen Inferior",
				accessor: "lower_margin",
				width: "18%",
				disableSortBy: true,
				Cell: ({ value }) => <span>{value != null ? `${value}%` : "—"}</span>,
			},
			{
				Header: "Margen Superior",
				accessor: "upper_margin",
				width: "18%",
				disableSortBy: true,
				Cell: ({ value }) => <span>{value != null ? `${value}%` : "—"}</span>,
			},
			{
				Header: "Estado",
				accessor: "is_active",
				width: "15%",
				disableSortBy: true,
				Cell: ({ value }) => (
					<CustomBadge variant={value ? "success" : "neutral"} dot>
						{value ? "Activo" : "Inactivo"}
					</CustomBadge>
				),
			},
			{
				Header: "Acción",
				disableSortBy: true,
				id: "actions",
				width: "10%",
				Cell: ({ row }) => {
					const { id, estate_id } = row.original;
					const url = id ? `/configuracion/tolerancias/editar/${id}` : `/configuracion/tolerancias/crear/${estate_id}`;
					return <CustomDropdown title="Acciones" items={[{ label: "Editar", action: () => navigate(url), icon: Pencil }]} align="right" />;
				},
			},
		],
		[tableState, navigate],
	);

	return (
		<ListScaffold
			titleBreadcrumbs={[
				{ label: "Dashboard", onClick: () => navigate("/") },
				{ label: "Configuración", onClick: () => navigate("/configuracion?section=orderApplication") },
				{ label: "Tolerancias" },
			]}
			subtitle="Configura los márgenes de tolerancia por bodega para el cierre de órdenes de aplicación"
			isLoading={isLoadingLookups || isLoadingTolerances}
			data={rows}
			totalItems={rows?.length}
			totalPages={1}
			toItem={rows?.length}
			fromItem={rows?.length > 0 ? 1 : 0}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[20, 50, 100]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
		>
			<ToleranceForm filters={filters} />
		</ListScaffold>
	);
};

export default Tolerances;
